import React, { useMemo, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik, FormikProps } from "formik";
import LabeledDateRangeInput from "../../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import Table from "../../../../ui-components/table/table";
import XGSErrorMessage from "../../../../ui-components/error-message/errorMessage";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import {
  EquipmentLogFilterModel,
  EquipmentLogFilterSchema,
  EquipmentLogItem,
} from "app/data/equipment/models";
import { useEquipmentChangeLog } from "hooks/useEquipment";
import "./equipmentChangeLog.scss";

const initialValues: EquipmentLogFilterModel = {
  dateFrom: undefined,
  dateTo: undefined,
};

interface EquipmentChangeLogProps {
  equipmentId: string;
}

const columnHelper = createColumnHelper<any>();

const EquipmentChangeLog: React.FC<EquipmentChangeLogProps> = ({ equipmentId }) => {
  const filterFormRef = useRef<FormikProps<EquipmentLogFilterModel>>(null);
  const [filter, setFilter] = useState<EquipmentLogFilterModel>(initialValues);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useEquipmentChangeLog(equipmentId, filter, !!equipmentId);

  const columns = [
    columnHelper.accessor("changedAt", {
      id: "changedAt",
      header: "Timestamp",
      cell: (cellProps) => {
        const value = cellProps.getValue();
        if (!value) return null;
        return (
          <div className="xgs-equipment-changelog__date">
            <div>
              {typeof value.toUiDateFormat === "function"
                ? value.toUiDateFormat()
                : new Date(value).toLocaleDateString()}
            </div>
            <div className="xgs-equipment-changelog__gray-value">
              {typeof value.toUiTimeShortFormat === "function"
                ? value.toUiTimeShortFormat()
                : new Date(value).toLocaleTimeString()}
            </div>
          </div>
        );
      },
      size: 140,
      minSize: 120,
    }),
    columnHelper.accessor("modifiedBy", {
      id: "modifiedBy",
      header: "User",
      size: 140,
      minSize: 100,
    }),
    columnHelper.accessor("action", {
      id: "action",
      header: "Action",
      cell: (cellProps) => {
        const action = String(cellProps.getValue() ?? "").toUpperCase();
        const actionColorMap: Record<string, TagColor> = {
          UPDATE: TagColor.ORANGE,
          CREATE: TagColor.GREEN,
          DEACTIVATE: TagColor.RED,
          DELETE: TagColor.RED,
        };
        const actionLabels: Record<string, string> = {
          CREATE: "Create",
          UPDATE: "Update",
          DELETE: "Deactivate",
          DEACTIVATE: "Deactivate",
        };
        const label = actionLabels[action] || cellProps.getValue();
        return (
          <Tag mods={{ color: actionColorMap[action] || TagColor.GREY }}>
            {label}
          </Tag>
        );
      },
      size: 80,
      minSize: 70,
    }),
    columnHelper.accessor("changeDescription", {
      id: "changeDescription",
      header: "Change Description",
      size: 280,
      minSize: 160,
    }),
  ];

  const tableRows = useMemo(() => {
    const logs: EquipmentLogItem[] = data?.allItems || [];
    const rows: Array<{
      changedAt: string;
      modifiedBy: string;
      action: string;
      changeDescription: string;
    }> = [];

    logs.forEach((item) => {
      const lines = (item.changeDescription || "")
        .split("\n")
        .filter((line) => line.trim() !== "");

      if (lines.length === 0) {
        rows.push({
          changedAt: item.changedAt,
          modifiedBy: item.modifiedBy,
          action: item.action,
          changeDescription: "",
        });
        return;
      }

      lines.forEach((line) => {
        rows.push({
          changedAt: item.changedAt,
          modifiedBy: item.modifiedBy,
          action: item.action,
          changeDescription: line.replace(/-->/g, "→"),
        });
      });
    });

    return rows;
  }, [data?.allItems]);

  const onFilterSubmit = (formData: EquipmentLogFilterModel) => {
    const formattedFilter: EquipmentLogFilterModel = {
      dateFrom: formData.dateFrom
        ? (formData.dateFrom as any).toApiDateFormat
          ? (formData.dateFrom as any).toApiDateFormat()
          : formData.dateFrom
        : undefined,
      dateTo: formData.dateTo
        ? (formData.dateTo as any).toApiDateFormat
          ? (formData.dateTo as any).toApiDateFormat()
          : formData.dateTo
        : undefined,
    };
    setFilter(formattedFilter);
  };

  return (
    <div className="xgs-equipment-changelog">
      <div className="xgs-equipment-changelog__header">Change Log</div>
      <div className="xgs-equipment-changelog__grid">
        <Formik
          initialValues={initialValues}
          onSubmit={onFilterSubmit}
          validationSchema={EquipmentLogFilterSchema}
          innerRef={filterFormRef}
        >
          {(props: FormikProps<EquipmentLogFilterModel>) => (
            <Form>
              <div className="xgs-equipment-changelog__controls__search">
                <LabeledDateRangeInput
                  label="Filter by Date Range:"
                  labelMode={LabelModes.column}
                  start={props.values.dateFrom}
                  end={props.values.dateTo}
                  maxDate={new Date()}
                  onStartChange={(v) => {
                    props.setFieldValue("dateFrom", v || undefined);
                    props.submitForm();
                  }}
                  onEndChange={(v) => {
                    props.setFieldValue("dateTo", v || undefined);
                    props.submitForm();
                  }}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div className="xgs-equipment-changelog__table-wrapper">
        {!isError && (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={tableRows}
            rowHeight={48}
            infiniteScroll
            noResultsText="No changelog entries found for the selected date range."
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            minTableHeight={180}
          />
        )}
      </div>

      {isError && (
        <XGSErrorMessage className="xgs-equipment-changelog__error">
          {(error as any)?.message || "Error while obtaining the log"}
        </XGSErrorMessage>
      )}
    </div>
  );
};

export default EquipmentChangeLog;
